import React, { useState, useEffect } from 'react';

interface UserData {
  id: string;
  username: string;
  email: string;
  created_at: string;
  wallet: {
    ahn: number;
    urja: number;
  };
}

function App() {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get JWT token from localStorage (set by main Ahantravyuh app)
      const token = localStorage.getItem('token');

      if (!token) {
        setError('No authentication token found. Please login via the main Ahantravyuh app.');
        setLoading(false);
        return;
      }

      console.log('Fetching user data with token...');

      const response = await fetch('/ushna/api/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`Failed to fetch user data: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('User data received:', data);

      setUser(data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-400 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-400 rounded"></div>
                <div className="h-4 bg-gray-400 rounded w-5/6"></div>
              </div>
            </div>
          </div>
          <p className="mt-4 text-gray-600 text-center">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 p-8 rounded-lg shadow-md max-w-md">
          <h2 className="text-red-800 text-xl font-bold mb-4">Authentication Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchUserData}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <p className="text-gray-600">No user data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Ushna</h1>
          <p className="text-gray-600">Authentication & Wallet Verification Test</p>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">User Information</h2>

          <div className="space-y-3">
            <div className="flex items-center">
              <span className="font-semibold text-gray-700 w-32">Username:</span>
              <span className="text-gray-900 text-lg">{user.username}</span>
            </div>

            <div className="flex items-center">
              <span className="font-semibold text-gray-700 w-32">Email:</span>
              <span className="text-gray-600">{user.email}</span>
            </div>

            <div className="flex items-center">
              <span className="font-semibold text-gray-700 w-32">User ID:</span>
              <span className="text-gray-500 text-sm font-mono">{user.id}</span>
            </div>
          </div>
        </div>

        {/* Wallet Balances Card */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-yellow-200 pb-2">Wallet Balances</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* AHN Balance */}
            <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">AHN Balance</p>
                  <p className="text-3xl font-bold text-yellow-600 mt-1">
                    {user.wallet.ahn.toFixed(2)}
                  </p>
                </div>
                <div className="text-5xl text-yellow-400">
                  <span role="img" aria-label="coin">🪙</span>
                </div>
              </div>
            </div>

            {/* URJA Balance */}
            <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">URJA Balance</p>
                  <p className="text-3xl font-bold text-orange-600 mt-1">
                    {user.wallet.urja.toFixed(2)}
                  </p>
                </div>
                <div className="text-5xl text-orange-400">
                  <span role="img" aria-label="energy">⚡</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 font-semibold text-center">
            ✅ Authentication & Database Connection Verified Successfully!
          </p>
          <p className="text-green-600 text-sm text-center mt-2">
            JWT authentication is working and wallet balances are loading correctly from the database.
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
